/**
 * 期限卡只读摘要抽屉（ADM-24，period-pass-admin-ui:SC-11 / admin-reversal-and-ledger-ui:SC-18）。
 *
 * 流水业务编号 `businessRefType=PERIOD_PASS` 点击下钻打开：
 * 学生、课程SKU、状态徽标（含派生已到期）、生效区间、期限天数、剩余天数（仅生效中）、来源订单号。
 * 凭证不存在（404）时经 onNotFound 回调宿主切换只读说明抽屉，不报错不白屏（对齐既有回退口径）。
 */
import { useEffect, useRef, useState } from 'react';
import { Descriptions, Drawer, Spin, Tag } from 'antd';
import { entitlementApi, type PeriodPassView } from '../../api/entitlement.api';
import { ApiError } from '../../api/transport';
import {
  periodPassDisplayStatusLabel,
  periodPassStatusColor,
} from '../../utils/period-ledger';

export interface PeriodPassSummaryDrawerProps {
  passId: string | null;
  onClose: () => void;
  /** 凭证不存在（404）时回调（宿主切换业务编号说明抽屉） */
  onNotFound: (passId: string) => void;
}

/** 期限卡只读摘要抽屉（区间/状态/来源订单，无任何写操作）。 */
export function PeriodPassSummaryDrawer({
  passId,
  onClose,
  onNotFound,
}: PeriodPassSummaryDrawerProps) {
  const [pass, setPass] = useState<PeriodPassView | null>(null);
  const [loading, setLoading] = useState(false);
  // onNotFound 经 ref 转发，调用方可传内联函数而不重触发加载
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;

  useEffect(() => {
    setPass(null);
    if (!passId) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const detail = await entitlementApi.getPeriodPass(passId);
        if (!cancelled) setPass(detail);
      } catch (err) {
        if (!cancelled && err instanceof ApiError && err.status === 404) {
          notFoundRef.current(passId);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [passId]);

  return (
    <Drawer
      title="期限卡摘要"
      open={Boolean(passId)}
      onClose={onClose}
      width={520}
      destroyOnHidden
    >
      {loading && <Spin />}
      {pass && (
        <Descriptions column={1} size="small" bordered data-testid="period-pass-summary">
          <Descriptions.Item label="学生">
            {pass.studentName ?? pass.studentId ?? '-'}
          </Descriptions.Item>
          <Descriptions.Item label="课程SKU">{pass.skuName}</Descriptions.Item>
          <Descriptions.Item label="状态">
            <Tag color={periodPassStatusColor(pass.displayStatus)}>
              {periodPassDisplayStatusLabel[pass.displayStatus] ?? pass.displayStatus}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="生效区间">
            {pass.startDate && pass.endDate
              ? `${pass.startDate} ~ ${pass.endDate}（含当天）`
              : '首次上课自动起算（不设起始日）'}
          </Descriptions.Item>
          <Descriptions.Item label="期限天数">{pass.periodDays} 天</Descriptions.Item>
          {pass.displayStatus === 'ACTIVE' && (
            <Descriptions.Item label="剩余天数">{pass.remainingDays} 天</Descriptions.Item>
          )}
          <Descriptions.Item label="来源订单号">{pass.sourceOrderId}</Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}
